import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Switch, 
  Button, 
  Card, 
  Row, 
  Col, 
  Divider,
  message,
  Space
} from 'antd';
import { 
  ArrowLeftOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { ApiConfig } from '../../services/ApiConfigs';
import planService from '../../services/planService';
import { routes } from '../../utils/routes.config';
import useFetch from '../../Hooks/FetchData';
import { Typography } from 'antd';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

// Currency options
const CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
];

const EditPlan = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('INR');

  const { data: plan, loading: planLoading } = useFetch(
    id ? `${ApiConfig.PLANS}/${id}` : null,
    { trigger: !!id }
  );

  useEffect(() => {
    if (plan) {
      form.setFieldsValue({
        ...plan,
        currency: plan.currency || 'INR',
      });
      setSelectedCurrency(plan.currency || 'INR');
    }
  }, [plan, form]);

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    const currencyData = CURRENCIES.find(c => c.code === currency);
    form.setFieldsValue({
      currency,
      currencySymbol: currencyData?.symbol || '₹',
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const res = await planService.updatePlan(id!, values);
      if (res?.success !== false) {
        message.success('Plan updated successfully');
        navigate(routes.PLANS);
      } else {
        message.error(res?.message || 'Failed to update plan');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to update plan');
    } finally {
      setLoading(false);
    }
  };

  if (planLoading) {
    return <MainLayout>Loading...</MainLayout>;
  }

  if (!plan) {
    return <MainLayout>Plan not found</MainLayout>;
  }

  return (
    <MainLayout>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(routes.PLANS)}
            >
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>Edit Plan</Title>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Plan Name"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="e.g., Basic Plan" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="planType"
                label="Plan Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="subscription">Subscription</Option>
                  <Option value="one_time">One-Time</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="billingPeriod"
                label="Billing Period"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="monthly">Monthly</Option>
                  <Option value="yearly">Yearly</Option>
                  <Option value="lifetime">Lifetime</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Plan description..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true }]}
              >
                <Select onChange={handleCurrencyChange}>
                  {CURRENCIES.map(curr => (
                    <Option key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="currencySymbol"
                label="Currency Symbol"
                rules={[{ required: true }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Plan Limits</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="maxWarehouses"
                label="Max Warehouses"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxDrivers"
                label="Max Drivers"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxOrders"
                label="Max Orders/Month"
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="Sort Order"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isActive"
                valuePropName="checked"
                label="Active"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isPopular"
                valuePropName="checked"
                label="Popular Plan"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 24 }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              size="large"
              icon={<SaveOutlined />}
              loading={loading}
            >
              Update Plan
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </MainLayout>
  );
};

export default EditPlan;

